import { useAccount } from 'wagmi';
import { useEffect, useState } from 'react';
import { logger } from '../utils/logger';
import { useUserAuth } from '@/providers/UserAuthProvider';

const ADMIN_ADDRESSES = [
  import.meta.env.VITE_ADMIN_ADDRESS_1?.toLowerCase(),
  import.meta.env.VITE_ADMIN_ADDRESS_2?.toLowerCase(),
  import.meta.env.VITE_ADMIN_ADDRESS_3?.toLowerCase(),
].filter(Boolean);

const ADMIN_EMAILS = [
  import.meta.env.VITE_ADMIN_EMAIL_1?.toLowerCase(),
  import.meta.env.VITE_ADMIN_EMAIL_2?.toLowerCase(),
  import.meta.env.VITE_ADMIN_EMAIL_3?.toLowerCase(),
].filter(Boolean);

export function useAdmin() {
  const { address } = useAccount();
  const { user } = useUserAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const verifyAdmin = async () => {
      const userEmail = user?.email?.toLowerCase();

      // Check email-based admin first (if user is signed in)
      if (userEmail && ADMIN_EMAILS.includes(userEmail)) {
        logger.debug('Email verified as admin via environment variables:', userEmail);
        setIsAdmin(true);
        setIsInitialized(true);
        return;
      }

      // Check wallet-based admin
      if (!address) {
        logger.debug('No wallet address found and email not admin');
        setIsAdmin(false);
        setIsInitialized(true);
        return;
      }

      logger.debug('Verifying admin status for address:', address);
      const normalizedAddress = address.toLowerCase();

      try {
        // First try local verification
        if (ADMIN_ADDRESSES.includes(normalizedAddress)) {
          logger.debug('Address verified as admin via environment variables');
          setIsAdmin(true);
          setIsInitialized(true);
          return;
        }

        // Then try Netlify function as backup
        try {
          const response = await fetch('/api/verify-admin', {
            method: 'POST',
            body: JSON.stringify({ address, email: userEmail }),
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const data = await response.json();
            logger.debug('Admin verification response:', data);
            setIsAdmin(data.isAdmin);
          } else {
            logger.debug('Falling back to environment variable check');
            setIsAdmin(ADMIN_ADDRESSES.includes(normalizedAddress));
          }
        } catch (error) {
          logger.error('API call failed, falling back to environment variable check');
          setIsAdmin(ADMIN_ADDRESSES.includes(normalizedAddress));
        }

        setIsInitialized(true);
      } catch (error) {
        logger.error('Error verifying admin status:', error);
        setIsAdmin(false);
        setIsInitialized(true);
      }
    };

    verifyAdmin();
  }, [address, user?.email]);

  return { isAdmin, isInitialized };
}
